import { Component, computed, effect, ElementRef, HostListener, inject, input, output, signal } from '@angular/core'
import { numberDisplay } from 'src/app/shared/services/filter/number-range-filter'
import { TextInputComponent } from '../text-input/text-input.component'
import { LabeledPropertyComponent } from '../labeled-property/labeled-property.component'

export interface NumberRange {
  min: number,
  max: number
}

@Component({
  selector: 'div[app-number-range-selector]',
  imports: [
    TextInputComponent,
    LabeledPropertyComponent,
  ],
  styles: `
    .wrapper {
        position: relative;
        width: 100%;
        padding: 0.5rem 0rem 0rem;
    }
    
    input {
        width: 100%;
        padding: 0.5rem 1.75rem 0.25rem 0.25rem;
        border: none;
        background: white;
        text-align: left;
        border-bottom: 1px solid black;
        font-size: 0.9rem;
        line-height: 1.25rem;

        &:focus {
            outline: none;
        }

    }
    
    .popover {
        display: none;
        position: absolute;
    }

    .popover.open {
        z-index: 3;
        display: flex;
        flex-direction: column;
        min-height: 7rem;
        width: 300px;
        right: 0;
        top: calc(100% + 0.5rem);
        border: 1px solid black;
        border-radius: 0;
        padding: 0.5rem 1rem;
        background: white;
        gap: 1rem;


        &::before {
            content: ' ';
            border: none;
            border-top: 1px solid black;
            border-right: 1px solid black;
            width: 0.5rem;
            height: 0.5rem;
            transform: rotate(-45deg);
            position: absolute;
            top: calc(-0.25rem - 1px);
            background: linear-gradient(
                45deg, 
                rgba(255,255,255,0) 0%, 
                rgba(255,255,255,0) calc(50% - 1px), 
                rgba(255,255,255,1) calc(50% - 1px), 
                rgba(255,255,255,1) 100%
                );
            right: 1rem;
        }

        .submit-btn {
            border: 1px solid black;
            border-radius: 0.25rem;
            background-color: green;
            color: white;
            margin-top: 1rem;

            &:disabled {
                opacity: 0.5;
            }
        }
    }`,
  template: `
    <div class="wrapper">
        <input
            type="button"
            [placeholder]="'none'"
            [value]="displayText()"
            [disabled]="disabled()"
            (click)="toggle()"
        />
        <div class="popover" [class.open]="open()">
            <div app-labeled-property [label]="allowRange() ? 'Min': 'Value'">
                <div app-text-input [value]="minValueStr()" (valueChange)="handleMinChange($event)"></div>
            </div>
            @if(allowRange()) {
              <div app-labeled-property label="Max">
                <div app-text-input [value]="maxValueStr()" (valueChange)="handleMaxChange($event)"></div>
              </div>
            }
            <button class="submit-btn" [disabled]="!changed()" (click)="setNumberRange()">Submit</button>
        </div>
    </div>`,
})
export class NumberRangeSelector {
  private elementRef = inject(ElementRef)

  public allowRange = input(true)
  public precision = input(8)
  public disabled = input(false)
  public value = input<NumberRange | undefined | null>({ min: NaN, max: NaN })

  public readonly valueChange = output<NumberRange>()

  protected readonly minValueStr = signal('')
  protected readonly maxValueStr = signal('')
  protected readonly open = signal(false)
  protected readonly changed = signal(false)

  protected readonly _value = computed(() => this.value() ?? { min: NaN, max: NaN })
  protected readonly regex = computed(() => {
    const p = this.precision()
    return p === 0 
      ? /\..*/
      : new RegExp(`(?<=(\\.\\d{${this.precision()}}))\\d+`)
  })

  protected readonly displayText = computed(() => {
    if(Number.isNaN(this._value().min) && Number.isNaN(this._value().max)){
      return 'None Selected'
    }
    if(Number.isNaN(this._value().min)) {
      return `≤ ${numberDisplay(this._value().max, this.precision())}`
    }
    if(Number.isNaN(this._value().max) && this.allowRange()) {
      return `≥ ${numberDisplay(this._value().min, this.precision())}`
    }
    if(Number.isNaN(this._value().max) && !this.allowRange()) {
      return `${numberDisplay(this._value().min, this.precision())}`
    }
    return `Between ${numberDisplay(this._value().min, this.precision())} & ${numberDisplay(this._value().max, this.precision())}`
  })

  constructor() {
    effect(() => {
      this.minValueStr.set(Number.isNaN(this._value().min) ? '': numberDisplay(this._value().min, this.precision()))
      this.maxValueStr.set(Number.isNaN(this._value().max) ? '': numberDisplay(this._value().max, this.precision()))
    })
  }

    
  @HostListener('document:click', ['$event'])
  handleOutsideClick(event: Event) {
    if (this.open() && !this.elementRef.nativeElement.contains(event.target as Node)) {
      this.toggle()
    }
  }

  toggle(): void {
    this.open.update(v => !v)
  }

  handleMinChange(evt: string) {
    const safe = evt.replace(this.regex(), '')
    this.minValueStr.set(safe)
    this.changed.set(true)
  }
    
  handleMaxChange(evt: string) {
    const safe = evt.replace(this.regex(), '')
    this.maxValueStr.set(safe)
    this.changed.set(true)
  }

  setNumberRange() {
    this.changed.set(false)
    this.valueChange.emit({ 
      min: parseFloat(this.minValueStr()), 
      max:parseFloat(this.maxValueStr()), 
    })
  }
}